import React, { useState, useRef, useEffect } from 'react'
import { X, CheckCircle, FileSignature } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import type { SignatureProvider, SigningSurfaceProps, SignatureResult } from '../types'

const InAppSigningSurface: React.FC<SigningSurfaceProps> = ({ request, onComplete, onCancel }) => {
  const [signature, setSignature] = useState('')
  const [drawing, setDrawing] = useState(false)
  const [signatureType, setSignatureType] = useState<'type' | 'draw'>('type')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hasDrawn, setHasDrawn] = useState(false)

  function readSavedSignature(): { signature: string; signatureMode: 'type' | 'draw' } | null {
    const raw = localStorage.getItem('user_saved_signature')
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      if (parsed?.signature && (parsed.signatureMode === 'type' || parsed.signatureMode === 'draw')) {
        return parsed
      }
    } catch {
      const legacyType = localStorage.getItem('user_saved_signature_type')
      if (legacyType === 'type' || legacyType === 'draw') {
        return { signature: raw, signatureMode: legacyType }
      }
    }
    return null
  }

  useEffect(() => {
    const saved = readSavedSignature()
    if (!saved) return
    setSignatureType(saved.signatureMode)
    if (saved.signatureMode === 'type') {
      setSignature(saved.signature)
    } else {
      setTimeout(() => {
        if (!canvasRef.current) return
        const ctx = canvasRef.current.getContext('2d')
        if (!ctx) return
        const img = new Image()
        img.onload = () => { ctx.drawImage(img, 0, 0); setHasDrawn(true) }
        img.src = saved.signature
      }, 100)
    }
  }, [])

  useEffect(() => {
    if (signatureType !== 'draw' || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#1e40af'
    const saved = readSavedSignature()
    if (saved && saved.signatureMode === 'draw') {
      const img = new Image()
      img.onload = () => { ctx.drawImage(img, 0, 0); setHasDrawn(true) }
      img.src = saved.signature
    }
  }, [signatureType])

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setDrawing(true)
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (ctx) { ctx.beginPath(); ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top) }
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return
    setHasDrawn(true)
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (ctx) { ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top); ctx.stroke() }
  }

  const stopDrawing = () => {
    setDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) { ctx.clearRect(0, 0, canvas.width, canvas.height); setHasDrawn(false) }
  }

  const clearSignature = () => {
    setSignature('')
    clearCanvas()
    localStorage.removeItem('user_saved_signature')
    localStorage.removeItem('user_saved_signature_type')
  }

  function saveSignatureForReuse(value: string, mode: 'type' | 'draw') {
    localStorage.setItem(
      'user_saved_signature',
      JSON.stringify({ signature: value, signatureMode: mode }),
    )
    localStorage.removeItem('user_saved_signature_type')
  }

  const handleSign = () => {
    if (signatureType === 'type' && signature.trim()) {
      const value = signature.trim()
      saveSignatureForReuse(value, 'type')
      const result: SignatureResult = {
        providerId: 'in-app',
        signatureValue: value,
        signatureType: 'typed',
        signedAt: new Date().toISOString(),
        signerName: request.signerName,
      }
      onComplete(result)
    } else if (signatureType === 'draw' && hasDrawn && canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL()
      saveSignatureForReuse(dataUrl, 'draw')
      const result: SignatureResult = {
        providerId: 'in-app',
        signatureValue: dataUrl,
        signatureType: 'drawn',
        signedAt: new Date().toISOString(),
        signerName: request.signerName,
      }
      onComplete(result)
    }
  }

  const canSubmit = signatureType === 'type' ? signature.trim().length > 0 : hasDrawn

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Your Signature</h3>
        <div className="flex items-center gap-3">
          {(signature || hasDrawn) && (
            <>
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center">
                <CheckCircle className="h-3 w-3 mr-1" />
                Will be saved for future documents
              </span>
              <Button type="button" variant="outline" size="sm" onClick={clearSignature} className="text-xs">
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={signatureType === 'type' ? 'default' : 'outline'}
          onClick={() => setSignatureType('type')}
          size="sm"
        >
          Type Signature
        </Button>
        <Button
          type="button"
          variant={signatureType === 'draw' ? 'default' : 'outline'}
          onClick={() => setSignatureType('draw')}
          size="sm"
        >
          Draw Signature
        </Button>
      </div>
      {signatureType === 'type' ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground/80">Type your full name</label>
          <Input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Enter your full legal name"
            className="text-2xl font-signature"
            style={{ fontFamily: 'cursive' }}
          />
          {signature && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg border-2 border-primary/20">
              <p className="text-xs text-muted-foreground/80 mb-2">Preview:</p>
              <p className="text-3xl font-signature" style={{ fontFamily: 'cursive' }}>{signature}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground/80">Draw your signature</label>
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="w-full border-2 border-border rounded-lg cursor-crosshair bg-background"
          />
          <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>Clear</Button>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-muted-foreground">
            <p className="font-medium mb-1">Electronic Signature Consent</p>
            <p>
              By clicking &ldquo;Sign Document&rdquo; below, you agree that your electronic signature is the legal
              equivalent of your manual signature on this document.
            </p>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-3 mt-6 flex justify-end space-x-3">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSign} disabled={!canSubmit}>
          <FileSignature className="h-4 w-4 mr-2" />
          Sign Document
        </Button>
      </div>
    </Card>
  )
}

export const inAppProvider: SignatureProvider = {
  id: 'in-app',
  displayName: 'In-App Signature',
  available() { return true },
  renderSigningSurface(props: SigningSurfaceProps) {
    return <InAppSigningSurface {...props} />
  },
}
